import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Icon,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import MDBox from "components/MDBox";
import AlwaysEditableField from "components/InlineEditableField/AlwaysEditableField";
import InlineEditableField from "components/InlineEditableField/InlineEditableField";
import UiButton from "components/base/UiButton";
import UiInput from "components/base/UiInput";
import UiTypography from "components/base/UiTypography";
import OrganizationProductTypeIdsOptions from "modules/OrganizationProductTypes/OrganizationProductTypeIdsOptions/OrganizationProductTypeIdsOptions";
import ToggleProposalProductOptionality from "modules/proposals/ToggleProposalProductOptionality";
import { ProposalProduct, WorksheetHeaderUnit, WorksheetPricingMode } from "generated/graphql";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import { draftColors } from "modules/proposals/draft/draftDesignTokens";
import { useMemo } from "react";
import { useDraftContext } from "modules/proposals/draft/DraftContext";
import WorksheetItemEditor from "./WorksheetItemEditor";
import {
  HEADER_UNIT_OPTIONS,
  dollarsToCents,
  getProposalItemFormValues,
  getRowEffectiveQuantity,
  parseDecimalInput,
  rawToEnum,
} from "./worksheetFormValues";
import { resolveHelpers } from "utils/worksheet/worksheetHelpers";
import { compactSelectSx, worksheetTableGridTemplate } from "./worksheetStyles";
import useWorksheetItemActions from "./useWorksheetItemActions";
import useInlineFinancialEdits, { parseCurrencyInput } from "./useInlineFinancialEdits";
import EditableFinancialCell, { ReadOnlyFinancialCell } from "./EditableFinancialCell";
import SaveWorksheetProductDialog from "./SaveWorksheetProductDialog";

type WorksheetItemRowProps = {
  product: ProposalProduct;
  isExpanded: boolean;
  onToggleExpand: (id: string) => void;
  onDeleteProduct: (id: string) => void;
};

export default function WorksheetItemRow({
  product,
  isExpanded,
  onToggleExpand,
  onDeleteProduct,
}: WorksheetItemRowProps) {
  const { isReadOnly } = useDraftContext();
  const {
    saving,
    saveProductOpen,
    setSaveProductOpen,
    productTypeId,
    setProductTypeId,
    internalName,
    setInternalName,
    savingProduct,
    handleSave,
    handleInlineUpdate,
    handleUpdateLibrary,
    handleSaveAsProduct,
    handleConfirmSaveProduct,
  } = useWorksheetItemActions(product);
  const defaultValues = useMemo(() => getProposalItemFormValues(product), [product]);
  const resolution = useMemo(() => resolveHelpers(defaultValues), [defaultValues]);
  const formulaLines = useMemo(() => {
    const lines: string[] = [];

    (defaultValues.costRows ?? []).forEach((row) => {
      if (row.sourceName) {
        lines.push(`Library: ${row.sourceName}`);
      }

      const helperQuantity = resolution.rowQuantities[row.rowUuid];

      if (row.quantitySource === "trucking") {
        lines.push(
          `Trucking planner: ${resolution.rowTrucks[row.rowUuid] ?? 0} truck(s) · ${
            helperQuantity ? helperQuantity.toDecimalPlaces(2).toString() : "0"
          } ${row.truckingUnit || "Hours"}`
        );
        return;
      }

      if (helperQuantity !== undefined) {
        lines.push(
          `${row.name || "Row"}: linked calculation → ${helperQuantity
            .toDecimalPlaces(2)
            .toString()} ${row.unit}`
        );
        return;
      }

      if (row.category === "MATERIAL" && row.calcMethod !== "MANUAL") {
        lines.push(
          `${row.name || "Material"}: ${row.calcMethod} calculation → ${getRowEffectiveQuantity(
            row,
            defaultValues.sellingQuantity,
            defaultValues.unit,
            resolution
          )} ${row.unit}`
        );
      }
    });

    return lines;
  }, [defaultValues, resolution]);

  const { costCents, saveRate, savePercentage, saveProfit, saveMargin, saveDiscount, saveTotal } =
    useInlineFinancialEdits(handleInlineUpdate, product);

  return (
    <>
      <MDBox
        display="grid"
        gridTemplateColumns={worksheetTableGridTemplate}
        alignItems="center"
        sx={{
          borderBottom: `1px solid ${draftColors.border}`,
          gridAutoRows: "32px",
          py: "6px",
          "&:hover": { backgroundColor: "#fafbfc" },
        }}
      >
        <MDBox
          sx={{
            cursor: "pointer",
            color: draftColors.textAction,
            display: "flex",
            justifyContent: "center",
          }}
          onClick={() => onToggleExpand(product.id)}
        >
          <Icon fontSize="small">{isExpanded ? "arrow_drop_down" : "chevron_right"}</Icon>
        </MDBox>

        <MDBox
          px="4px"
          minWidth={0}
          sx={{
            "& .editable-value": {
              display: "block",
              maxWidth: "100%",
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            },
          }}
        >
          <MDBox display="flex" alignItems="center" gap="2px">
            <MDBox flex={1} minWidth={0}>
              <InlineEditableField
                value={product.productName ?? product.material?.name ?? "New Item"}
                onSave={async (value) => {
                  await handleInlineUpdate({ name: value.trim() || "New Item" });
                }}
                disabled={isReadOnly}
                fontSize="12px"
                validate={(value) => value.trim().length > 0}
              />
            </MDBox>
            {formulaLines.length > 0 && (
              <Tooltip
                title={<span style={{ whiteSpace: "pre-line" }}>{formulaLines.join("\n")}</span>}
              >
                <Icon fontSize="small" sx={{ color: draftColors.textSecondary, cursor: "help" }}>
                  info_outline
                </Icon>
              </Tooltip>
            )}
          </MDBox>
          {product.worksheetProductSourceId && (
            <UiTypography
              uiVariant="body-sm"
              sx={{ fontSize: "9px", color: draftColors.textSecondary, px: "2px" }}
            >
              [Custom]
            </UiTypography>
          )}
        </MDBox>

        <MDBox px="4px">
          <AlwaysEditableField
            value={String(product.sellingQuantity ?? 0)}
            onSave={async (value) => {
              await handleInlineUpdate({ sellingQuantity: value });
              return value;
            }}
            disabled={isReadOnly}
            fontSize="12px"
            validate={(value) => /^\d*\.?\d*$/.test(value)}
          />
        </MDBox>
        <MDBox px="4px" display="flex" alignItems="center">
          <Select
            value={
              (rawToEnum(product.worksheetUnit) || WorksheetHeaderUnit.EACH) as WorksheetHeaderUnit
            }
            disabled={isReadOnly}
            fullWidth
            sx={{ ...compactSelectSx, display: "flex" }}
            onChange={(event) =>
              handleInlineUpdate({ unit: event.target.value as WorksheetHeaderUnit })
            }
          >
            {HEADER_UNIT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value} sx={{ fontSize: "11px" }}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </MDBox>
        <EditableFinancialCell
          value={(Number(product.sellingRate ?? 0) / 100).toFixed(2)}
          onSave={saveRate}
          disabled={isReadOnly}
        />
        <EditableFinancialCell
          kind="percentage"
          value={String(product.overheadPercentage ?? 0)}
          onSave={savePercentage("overheadPercentage")}
          disabled={isReadOnly}
        />
        <EditableFinancialCell
          kind="percentage"
          value={String(product.indirectPercentage ?? 0)}
          onSave={savePercentage("indirectPercentage")}
          disabled={isReadOnly}
        />
        <ReadOnlyFinancialCell cents={costCents} />
        <EditableFinancialCell
          value={(Number(product.profit ?? 0) / 100).toFixed(2)}
          onSave={saveProfit}
          disabled={isReadOnly}
        />
        <EditableFinancialCell
          kind="percentage"
          value={String(product.profitPercentage ?? 0)}
          onSave={saveMargin}
          disabled={isReadOnly}
        />
        <EditableFinancialCell
          value={(Number(product.discount ?? 0) / 100).toFixed(2)}
          onSave={saveDiscount}
          disabled={isReadOnly}
        />
        <EditableFinancialCell
          value={(
            Number(product.overallTotalAfterDiscount ?? product.overallTotal ?? 0) / 100
          ).toFixed(2)}
          onSave={saveTotal}
          disabled={isReadOnly}
        />

        <MDBox sx={{ display: "flex", justifyContent: "center" }}>
          <ToggleProposalProductOptionality id={product.id} isOptional={product.isOptional} />
        </MDBox>

        <MDBox display="flex" justifyContent="center" alignItems="center" gap="4px" pr="4px">
          {!isReadOnly && (
            <Tooltip title="Delete item">
              <MDBox
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  color: draftColors.red,
                  cursor: "pointer",
                }}
                onClick={() => onDeleteProduct(product.id)}
              >
                <Icon fontSize="small">delete</Icon>
              </MDBox>
            </Tooltip>
          )}
        </MDBox>
      </MDBox>

      {isExpanded && (
        <MDBox px="12px" pb="12px">
          <WorksheetItemEditor
            defaultValues={defaultValues}
            isReadOnly={isReadOnly}
            saving={saving}
            hideFinancialHeader
            onSave={handleSave}
            onSaveAsProduct={handleSaveAsProduct}
            onUpdateLibrary={
              !isReadOnly && product.worksheetProductSourceId ? handleUpdateLibrary : undefined
            }
          />
        </MDBox>
      )}

      <SaveWorksheetProductDialog
        open={saveProductOpen}
        loading={savingProduct}
        productTypeId={productTypeId}
        onProductTypeIdChange={(value) => setProductTypeId(value)}
        internalName={internalName}
        onInternalNameChange={(value) => setInternalName(value)}
        onClose={() => setSaveProductOpen(false)}
        onConfirm={handleConfirmSaveProduct}
      />
    </>
  );
}
